import { IconType, ISelectOption, SideToolbox, SideToolboxItem } from '@aon/aon-tws-common-ui';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ContentConstants from '../../../../contentConstants';
import { ElementType, IPageFlowElementModel, ISurveyEditModel, ISurveyElement } from '../../../../models';
import { ActionCreators as SurveyActionCreators } from '../../../../store/SurveyStore';
import { DescriptionTextModal } from './DescriptionTextModal';
import { PageBreakModal } from './PageBreakModal';
import { SectionBreakModal } from './SectionBreakModal';
import { SectionHeaderModal } from './SectionHeaderModal';

export type PageFlowContainerProps = {
	surveyEditModel: ISurveyEditModel;
	availableCultures: ISelectOption[];
	pageFlowElement?: IPageFlowElementModel;
	onClose: () => void;
};

export const PageFlowContainer = (props: PageFlowContainerProps) => {
	const dispatch = useDispatch();

	const [pageBreakModalOpen, setPageBreakModalOpen] = useState(false);
	const [sectionBreakModalOpen, setSectionBreakModalOpen] = useState(false);
	const [sectionHeaderModalOpen, setSectionHeaderModalOpen] = useState(false);
	const [descriptionTextModalOpen, setDescriptionTextModalOpen] = useState(false);
	const [elementToEdit, setElementToEdit] = useState<IPageFlowElementModel | undefined>(undefined);
	const [elements, setElements] = useState<ISurveyElement[]>([]);
	const [maxId, setMaxId] = useState(0);
	const [maxSequence, setMaxSequence] = useState<number | undefined>(undefined);

	useEffect(() => {
		setElements(([...props.surveyEditModel.pageFlowElements] as ISurveyElement[]).concat([...props.surveyEditModel.questions]));
	}, [props.surveyEditModel]);

	useEffect(() => {
		setElementToEdit(props.pageFlowElement);

		switch (props.pageFlowElement?.elementType) {
			case ElementType.PageBreak:
				togglePageBreakModal(props.pageFlowElement);
				break;
			case ElementType.SectionBreak:
				toggleSectionBreakModal(props.pageFlowElement);
				break;
			case ElementType.SectionHeader:
				toggleSectionHeaderModal(props.pageFlowElement);
				break;
			case ElementType.TextElement:
				toggleDescriptionTextModal(props.pageFlowElement);
				break;
		}
	}, [props.pageFlowElement]);

	useEffect(() => {
		setMaxId(Math.max(...elements.map((e) => e.id), 0));
		setMaxSequence(
			elements.length > 0
				? elements
						.filter((e) => e.sequence !== undefined)
						.map((e) => e.sequence)
						.reduce((prev, curr) => {
							if ((prev === undefined && curr === undefined) || prev === undefined) {
								return curr;
							} else if (curr === undefined) {
								return prev;
							}
							return prev < curr ? curr : prev;
						})
				: undefined,
		);
	}, [elements]);

	return (
		<>
			<SideToolbox hasSidePad={true}>
				<SideToolboxItem
					icon={IconType.pageBreak}
					onClick={() => togglePageBreakModal()}
					tooltip={ContentConstants.LOGIC_AND_LAYOUT.PAGE_BREAK}
				/>
				<SideToolboxItem
					icon={IconType.sectionHeader}
					onClick={() => toggleSectionHeaderModal()}
					tooltip={ContentConstants.LOGIC_AND_LAYOUT.SECTION_HEADER}
				/>
				<SideToolboxItem
					icon={IconType.sectionBreak}
					onClick={() => toggleSectionBreakModal()}
					tooltip={ContentConstants.LOGIC_AND_LAYOUT.SECTION_BREAK}
				/>
				<SideToolboxItem
					icon={IconType.text}
					onClick={() => toggleDescriptionTextModal()}
					tooltip={ContentConstants.LOGIC_AND_LAYOUT.TEXT}
				/>
			</SideToolbox>
			<PageBreakModal
				pageFlowElement={elementToEdit}
				questions={props.surveyEditModel.questions}
				isOpen={pageBreakModalOpen}
				elementType={ElementType.PageBreak}
				maxId={maxId}
				onClose={() => togglePageBreakModal()}
				onSubmit={onSavePageBreak}
			/>
			<SectionBreakModal
				pageFlowElement={elementToEdit}
				questions={props.surveyEditModel.questions}
				isOpen={sectionBreakModalOpen}
				elementType={ElementType.SectionBreak}
				maxId={maxId}
				onClose={() => toggleSectionBreakModal()}
				onSubmit={onSaveSectionBreak}
			/>
			<SectionHeaderModal
				pageFlowElement={elementToEdit}
				questions={props.surveyEditModel.questions}
				isOpen={sectionHeaderModalOpen}
				elementType={ElementType.SectionHeader}
				maxId={maxId}
				supportedCultures={props.surveyEditModel.supportedCultures}
				availableCultures={props.availableCultures}
				defaultCultureCode={props.surveyEditModel.defaultCultureCode}
				onClose={() => toggleSectionHeaderModal()}
				onSubmit={onSaveSectionHeader}
			/>
			{descriptionTextModalOpen && (
				<DescriptionTextModal
					pageFlowElement={elementToEdit}
					questions={props.surveyEditModel.questions}
					isOpen={descriptionTextModalOpen}
					elementType={ElementType.TextElement}
					maxId={maxId}
					maxSequence={maxSequence}
					supportedCultures={props.surveyEditModel.supportedCultures}
					availableCultures={props.availableCultures}
					defaultCultureCode={props.surveyEditModel.defaultCultureCode}
					onClose={() => toggleDescriptionTextModal()}
					onSubmit={onSaveDescriptionText}
				/>
			)}
		</>
	);

	function togglePageBreakModal(pageBreak?: IPageFlowElementModel) {
		setElementToEdit(pageBreak);
		onClose(pageBreakModalOpen);
		setPageBreakModalOpen(!pageBreakModalOpen);
	}
	function onSavePageBreak(pageBreaks: IPageFlowElementModel[]) {
		togglePageBreakModal();
		dispatch(SurveyActionCreators.SubmitPageFlowElementModal(pageBreaks));
	}
	function toggleSectionBreakModal(sectionBreak?: IPageFlowElementModel) {
		setElementToEdit(sectionBreak);
		onClose(sectionBreakModalOpen);
		setSectionBreakModalOpen(!sectionBreakModalOpen);
	}
	function onSaveSectionBreak(sectionBreaks: IPageFlowElementModel[]) {
		toggleSectionBreakModal();
		dispatch(SurveyActionCreators.SubmitPageFlowElementModal(sectionBreaks));
	}
	function toggleSectionHeaderModal(sectionHeader?: IPageFlowElementModel) {
		setElementToEdit(sectionHeader);
		onClose(sectionHeaderModalOpen);
		setSectionHeaderModalOpen(!sectionHeaderModalOpen);
	}
	function onSaveSectionHeader(sectionHeaders: IPageFlowElementModel[]) {
		toggleSectionHeaderModal();
		dispatch(SurveyActionCreators.SubmitPageFlowElementModal(sectionHeaders));
	}
	function toggleDescriptionTextModal(descriptionText?: IPageFlowElementModel) {
		setElementToEdit(descriptionText);
		onClose(descriptionTextModalOpen);
		setDescriptionTextModalOpen(!descriptionTextModalOpen);
	}
	function onSaveDescriptionText(descriptionTexts: IPageFlowElementModel[]) {
		toggleDescriptionTextModal();
		dispatch(SurveyActionCreators.SubmitPageFlowElementModal(descriptionTexts));
	}
	function onClose(isClosing: boolean) {
		if (isClosing) {
			props.onClose();
		}
	}
};
